import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { getNotifData, subscribeNotifs, refreshNotifs } from "@/lib/notifications";
import { fechaHora } from "@/lib/constants";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import { Bell, CheckCheck, AlertTriangle, Unlock, Inbox, CheckCircle2, XCircle } from "lucide-react";

export default function NotificationBell() {
  const navigate = useNavigate();
  const [data, setData] = useState(getNotifData);
  const [open, setOpen] = useState(false);

  useEffect(() => subscribeNotifs(setData), []);

  const abrir = async (n) => {
    if (!n.leida) {
      await api.post(`/notificaciones/${n.id}/leer`).catch(() => {});
    }
    setOpen(false);
    navigate(n.link || `/modelos/${n.modelo_id}`);
    refreshNotifs(true);
  };

  const leerTodas = async () => {
    await api.post("/notificaciones/leer-todas").catch(() => {});
    refreshNotifs(true);
  };

  return (
    <Popover open={open} onOpenChange={(o) => { setOpen(o); if (o) refreshNotifs(); }}>
      <PopoverTrigger asChild>
        <button className="relative p-2 rounded-lg text-slate-500 hover:bg-slate-100 transition-colors" data-testid="notif-bell">
          <Bell size={18} />
          {data.no_leidas > 0 && (
            <span className="absolute -top-0.5 -right-0.5 h-4 min-w-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notif-badge">
              {data.no_leidas > 9 ? "9+" : data.no_leidas}
            </span>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-96 p-0" data-testid="notif-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100">
          <span className="font-head font-bold">Notificaciones</span>
          {data.notificaciones.length > 0 && (
            <button onClick={leerTodas} className="text-xs text-blue-600 flex items-center gap-1 hover:text-blue-700" data-testid="notif-mark-all">
              <CheckCheck size={13} /> Marcar todas
            </button>
          )}
        </div>
        <div className="max-h-96 overflow-y-auto thin-scroll">
          {data.notificaciones.length === 0 ? (
            <p className="text-sm text-slate-400 text-center py-10">Sin notificaciones.</p>
          ) : (
            data.notificaciones.map((n) => {
              const ICONS = { atorado: AlertTriangle, habilitado: Unlock, solicitud: Inbox, solicitud_aprobada: CheckCircle2, solicitud_rechazada: XCircle };
              const TINTS = { atorado: "text-amber-600 bg-amber-50", habilitado: "text-blue-600 bg-blue-50", solicitud: "text-violet-600 bg-violet-50", solicitud_aprobada: "text-emerald-600 bg-emerald-50", solicitud_rechazada: "text-red-600 bg-red-50" };
              const Icon = ICONS[n.tipo] || Bell;
              const tint = TINTS[n.tipo] || "text-slate-600 bg-slate-50";
              return (
                <button
                  key={n.id}
                  onClick={() => abrir(n)}
                  data-testid={`notif-item-${n.id}`}
                  className={`w-full text-left flex gap-3 px-4 py-3 border-b border-slate-50 hover:bg-slate-50 transition-colors ${n.leida ? "opacity-60" : ""}`}
                >
                  <div className={`h-8 w-8 rounded-lg flex items-center justify-center shrink-0 ${tint}`}>
                    <Icon size={15} />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm text-slate-700 leading-snug">{n.mensaje}</p>
                    <span className="text-[11px] text-slate-400">{fechaHora(n.created_at)}</span>
                  </div>
                  {!n.leida && <span className="h-2 w-2 rounded-full bg-blue-500 shrink-0 mt-1.5" />}
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
